import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  useDeferredValue,
  memo,
} from "react";
import { ColumnFilterRow, applyColumnFilters } from "../../Components/Common/DashboardKit";
import useTableSort from "../../Components/Common/DashboardKit/useTableSort";
import SortableTh from "../../Components/Common/DashboardKit/SortableTh";
import exportRowsToCsv from "../../Components/Common/DashboardKit/exportCsv";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  InputGroup,
  InputGroupText,
  Row,
  Table,
} from "reactstrap";
import { useAppDialog } from "./AppDialogProvider";
import { resolveEmployeeNames } from "./mafiaIdentity";
import { partRevisionText } from "./mafiaPrintHeading";
import { MafiaPageHeader, TableSkeleton, EmptyState } from "./_shared";
import MafiaTheme from "./MafiaTheme";
import { ResponsiveTabs } from "../../Components/Common/DashboardKit";
import classNames from "classnames";
import api from "../../api";
import ThemedSelect from "../../Components/Common/ThemedSelect";

const PAGE_SIZE_OPTIONS = [50, 100, 200];
const DEFAULT_PAGE_SIZE = 100;

// Owner 2026-10-12: coming back from a part must show the list the way it was
// left (tab, search, column filters, sort, page). The screen is unmounted while
// the part is open, so its view state is kept here for the session.
const VIEW_MEMO_KEY = "mafia.allParts.view";
function readViewMemo() {
  try {
    return JSON.parse(sessionStorage.getItem(VIEW_MEMO_KEY) || "null") || {};
  } catch {
    return {};
  }
}

export default function AllParts({ onBack, onOpenEditPart }) {
  const dialog = useAppDialog();
  const viewMemo = useRef(readViewMemo()).current;
  const mountedRef = useRef(true);
  const rowRefs = useRef({});

  const [activeTab, setActiveTab] = useState(viewMemo.activeTab === "inprogress" ? "inprogress" : "all");

  const [allRows, setAllRows] = useState([]);
  const [inProgressRows, setInProgressRows] = useState([]);

  // Server-side pagination for the All Parts tab (scales to live pluto size):
  // the All tab now fetches one page at a time from the server with server-side
  // search + sort, so it never loads the whole MafiaTables into the browser.
  // totalCount drives the pager. The In-Progress tab keeps its existing
  // client-side path (it is a small set).
  const [allTotal, setAllTotal] = useState(0);
  const [allServerSort, setAllServerSort] = useState(
    viewMemo.allServerSort?.key ? viewMemo.allServerSort : { key: "partNumber", dir: "asc" }
  );
  const [exporting, setExporting] = useState(false);

  const [allLoaded, setAllLoaded] = useState(false);
  const [inProgressLoaded, setInProgressLoaded] = useState(false);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [searchAllInput, setSearchAllInput] = useState(viewMemo.searchAll || "");
  const [searchInProgressInput, setSearchInProgressInput] = useState(viewMemo.searchInProgress || "");
  const [searchAll, setSearchAll] = useState(viewMemo.searchAll || "");
  const [searchInProgress, setSearchInProgress] = useState(viewMemo.searchInProgress || "");

  // Round 9 (Batch 3): the All tab is server-paged, so its per-column filters
  // must be sent to the server (client-side filtering would only narrow the
  // loaded page, not the full catalog). columnFilters holds the live input;
  // serverColumnFilters is the debounced copy actually sent to the API.
  const [serverColumnFilters, setServerColumnFilters] = useState(viewMemo.columnFilters || {});
  // Engineers 2026-10-14 (Abha): "the approved version is the one that needs to be
  // visible with all the other approved revisions". The All tab shows RELEASED
  // revisions unless the person switches to every revision; a typed Released filter
  // still wins. Unverified and verified-but-unreleased tables keep their own pages.
  const [approvedOnly, setApprovedOnly] = useState(viewMemo.approvedOnly !== false);
  const effectiveColumnFilters = useMemo(
    () => (approvedOnly ? { released: "yes", ...(serverColumnFilters || {}) } : (serverColumnFilters || {})),
    [approvedOnly, serverColumnFilters]
  );

  const [selectedRowKeyAll, setSelectedRowKeyAll] = useState(viewMemo.selectedRowKeyAll || "");
  const [selectedRowKeyInProgress, setSelectedRowKeyInProgress] = useState("");
  const [selectedRowAll, setSelectedRowAll] = useState(null);
  const [selectedRowInProgress, setSelectedRowInProgress] = useState(null);

  const [pageAll, setPageAll] = useState(Number(viewMemo.pageAll) > 0 ? Number(viewMemo.pageAll) : 1);
  const [pageInProgress, setPageInProgress] = useState(1);
  const [pageSize, setPageSize] = useState(Number(viewMemo.pageSize) > 0 ? Number(viewMemo.pageSize) : DEFAULT_PAGE_SIZE);
  // what the person typed last (so a mount with the remembered value is not a "change")
  const lastSearchAllRef = useRef(viewMemo.searchAll || "");
  const lastSearchInProgressRef = useRef(viewMemo.searchInProgress || "");
  const lastColumnFiltersJsonRef = useRef(JSON.stringify(viewMemo.columnFilters || {}));
  const tabEffectRanRef = useRef(false);

  useEffect(() => {
    try {
      sessionStorage.setItem(VIEW_MEMO_KEY, JSON.stringify({
        activeTab, searchAll, searchInProgress, allServerSort, pageAll, pageSize,
        columnFilters: serverColumnFilters, selectedRowKeyAll, approvedOnly,
      }));
    } catch {
      /* storage unavailable: the list simply starts fresh next time */
    }
  }, [activeTab, searchAll, searchInProgress, allServerSort, pageAll, pageSize, serverColumnFilters, selectedRowKeyAll, approvedOnly]);

  const allSearchRef = useRef(null);
  const inProgressSearchRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    // Note: the All tab's initial load is handled by the page/search/sort
    // effect below (it fires on mount because activeTab === "all").
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const next = searchAllInput.trim();
      if (next === lastSearchAllRef.current) return;   // remembered value, not a change
      lastSearchAllRef.current = next;
      setSearchAll(next);
      setPageAll(1);
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [searchAllInput]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const next = searchInProgressInput.trim();
      if (next === lastSearchInProgressRef.current) return;
      lastSearchInProgressRef.current = next;
      setSearchInProgress(next);
      setPageInProgress(1);
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [searchInProgressInput]);

  useEffect(() => {
    const ref = activeTab === "all" ? allSearchRef.current : inProgressSearchRef.current;
    if (ref) {
      ref.focus();
      ref.select();
    }
  }, [activeTab]);

  useEffect(() => {
    // All tab is driven by the page/search/sort effect; only lazy-load
    // In-Progress here on first switch.
    if (activeTab === "inprogress" && !inProgressLoaded) {
      loadTabData("inprogress", false, true);
    }
  }, [activeTab, inProgressLoaded]);

  const deferredSearchAll = useDeferredValue(searchAll);
  const deferredSearchInProgress = useDeferredValue(searchInProgress);

  const loadTabData = useCallback(
    async (tab, showSpinner = true, forceRefresh = false) => {
      try {
        if (showSpinner) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        // In-Progress keeps its client-side cache; All tab is always
        // server-fetched (one page) so the cache short-circuit only applies
        // to In-Progress.
        if (tab === "inprogress" && inProgressLoaded && !forceRefresh) {
          return;
        }

        if (tab === "all") {
          // Server-side page fetch with server sort + per-column filters (R9).
          const { items, total } = await getEditPartsList({
            page: pageAll,
            pageSize,
            search: searchAll,
            sort: allServerSort.key,
            sortDir: allServerSort.dir,
            filters: effectiveColumnFilters,
          });
          if (!mountedRef.current) return;

          const built = (items || []).map((item) => buildRow(item, true));
          setAllRows(built);
          setAllTotal(total || 0);
          setAllLoaded(true);

          setSelectedRowKeyAll((prev) => {
            if (!prev) return "";
            const existing = built.find((row) => row.rowKey === prev) || null;
            setSelectedRowAll(existing);
            return existing ? prev : "";
          });

          // Round 8 (Batch 2): resolve Created By / Last Revised By ids -> names
          // in the background and re-set the rows with the resolved display
          // fields. The grid already showed raw ids above (fast first paint);
          // this upgrades them to "First Last (id)" a moment later. Guarded by
          // mountedRef so it no-ops if the user navigated away.
          enrichRowsWithNames(built).then((enriched) => {
            if (!mountedRef.current) return;
            if (enriched !== built) setAllRows(enriched);
          });
        } else {
          const safeRows = (await getEditPartsListInProgress()) || [];
          if (!mountedRef.current) return;

          setInProgressRows(safeRows);
          setInProgressLoaded(true);

          setSelectedRowKeyInProgress((prev) => {
            if (!prev) return "";
            const existing = safeRows.find((row) => row.rowKey === prev) || null;
            setSelectedRowInProgress(existing);
            return existing ? prev : "";
          });

          // Round 8 (Batch 2): background name resolution for the In-Progress tab.
          enrichRowsWithNames(safeRows).then((enriched) => {
            if (!mountedRef.current) return;
            if (enriched !== safeRows) setInProgressRows(enriched);
          });
        }
      } catch (error) {
        console.error(`Failed to load ${tab} parts:`, error);
        await dialog.error("Failed to load data.");
      } finally {
        if (mountedRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [inProgressLoaded, dialog, pageAll, pageSize, searchAll, allServerSort, effectiveColumnFilters]
  );

  // All tab: re-fetch from the server whenever page / search / sort / column
  // filters change (R9).
  useEffect(() => {
    if (activeTab !== "all") return;
    loadTabData("all", false, true);
  }, [pageAll, pageSize, searchAll, allServerSort, effectiveColumnFilters, activeTab, loadTabData]);

  const currentSearchRef = activeTab === "all" ? allSearchRef : inProgressSearchRef;
  const currentSearchInput = activeTab === "all" ? searchAllInput : searchInProgressInput;
  const currentRowsSource = activeTab === "all" ? allRows : inProgressRows;
  const selectedRowKey = activeTab === "all" ? selectedRowKeyAll : selectedRowKeyInProgress;
  const selectedRow = activeTab === "all" ? selectedRowAll : selectedRowInProgress;

  const filteredAllRows = useMemo(() => {
    const search = deferredSearchAll.toUpperCase();
    if (!search) return allRows;
    return allRows.filter((row) => row.searchText.includes(search));
  }, [allRows, deferredSearchAll]);

  const filteredInProgressRows = useMemo(() => {
    const search = deferredSearchInProgress.toUpperCase();
    if (!search) return inProgressRows;
    return inProgressRows.filter((row) => row.searchText.includes(search));
  }, [inProgressRows, deferredSearchInProgress]);

  const currentFilteredRows =
    activeTab === "all" ? filteredAllRows : filteredInProgressRows;

  // Round 35 — EPO-style per-column filters + click-to-sort. The quick
  // search above stays: it scans the precomputed searchText index, which
  // is the fast path for this very large dataset; the filter row adds
  // precise per-column narrowing on top.
  // (the remembered filters belong to the All tab; In-Progress starts clean)
  const [columnFilters, setColumnFilters] = React.useState(
    viewMemo.activeTab === "inprogress" ? {} : (viewMemo.columnFilters || {})
  );

  // Round 9 (Batch 3): debounce the All tab's column filters into the value
  // actually sent to the server, and reset to page 1 when they change. The
  // In-Progress tab filters client-side (small set) so it ignores this.
  useEffect(() => {
    if (activeTab !== "all") return;
    const t = setTimeout(() => {
      const json = JSON.stringify(columnFilters || {});
      if (json === lastColumnFiltersJsonRef.current) return;   // remembered value, not a change
      lastColumnFiltersJsonRef.current = json;
      setServerColumnFilters(columnFilters || {});
      setPageAll(1);
    }, 300);
    return () => clearTimeout(t);
  }, [columnFilters, activeTab]);

  // When switching AWAY from All (or resetting), clear the live filter inputs so
  // the In-Progress tab's client filter starts clean and vice-versa. Not on
  // mount: the remembered filters must survive coming back from a part.
  useEffect(() => {
    if (!tabEffectRanRef.current) {
      tabEffectRanRef.current = true;
      return;
    }
    setColumnFilters({});
  }, [activeTab]);

  const mafiaColumns = React.useMemo(() => {
    const cols = [
      { id: "sel",  label: "Select", type: "none" },
      { id: "partNumber",    label: "Part Number",    type: "text", get: (r) => r.partNumber || "" },
      // 2026-10-01 (tester): the same words and the same two digits as the printout.
      { id: "printRevision", label: "Part Revision", type: "text", get: (r) => partRevisionText(r.printRevision) },
      { id: "mafiaRevision", label: "MAFIA Revision", type: "text", get: (r) => String(r.mafiaRevision ?? "") },
      { id: "createdBy",     label: "Created By",     type: "text", get: (r) => r.createdByDisplay || r.createdBy || "" },
    ];
    if (activeTab === "all") cols.push({ id: "lastRevisedBy", label: "Last Revised By", type: "text", get: (r) => r.lastRevisedByDisplay || r.lastRevisedBy || "" });
    // Who verified the table (read from the history of the page's rows; display only).
    cols.push({ id: "verifiedBy", label: "Verified By", type: "none", get: (r) => r.verifiedByDisplay || r.verifiedBy || "" });
    cols.push(
      { id: "displayTimeStamp", label: "Timestamp", type: "date", get: (r) => r.displayTimeStamp || "" },
      { id: "verified", label: "Verified", type: "text", get: (r) => String(r.verified ?? "") },
      { id: "released", label: "Released", type: "text", get: (r) => String(r.released ?? "") },
    );
    return cols;
  }, [activeTab]);

  // Round 9: the All tab is server-filtered — allRows already reflects the
  // column filters, so we do NOT client-filter it again (that would wrongly
  // narrow to the loaded page). Only In-Progress applies client column filters.
  const columnFilteredRows = activeTab === "all"
    ? currentFilteredRows
    : applyColumnFilters(currentFilteredRows, mafiaColumns, columnFilters);
  const { sorted: clientSortedRows, sort, toggleSort } = useTableSort(columnFilteredRows, mafiaColumns);

  // All tab is server-paged: allRows already IS the current page (server did
  // search + sort + paging), so render it as-is. In-Progress stays client-side.
  const isServerPaged = activeTab === "all";
  const viewRows = isServerPaged ? allRows : clientSortedRows;

  const currentPage = activeTab === "all" ? pageAll : pageInProgress;
  const setCurrentPage = activeTab === "all" ? setPageAll : setPageInProgress;

  const effectiveTotal = isServerPaged ? allTotal : viewRows.length;
  const totalPages = Math.max(1, Math.ceil(effectiveTotal / pageSize));

  useEffect(() => {
    if (effectiveTotal === 0) return;   // nothing loaded yet: keep the remembered page
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages, setCurrentPage, effectiveTotal]);

  // For server-paged All tab, the rows returned are already the page; for the
  // client In-Progress tab, slice locally.
  const pagedRows = useMemo(() => {
    if (isServerPaged) return viewRows;
    const start = (currentPage - 1) * pageSize;
    return viewRows.slice(start, start + pageSize);
  }, [isServerPaged, viewRows, currentPage, pageSize]);

  const pageStart = effectiveTotal === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const pageEnd = isServerPaged
    ? Math.min((currentPage - 1) * pageSize + viewRows.length, effectiveTotal)
    : Math.min(currentPage * pageSize, effectiveTotal);

  // Sort header binding: on the server-paged All tab, header clicks update the
  // server sort (and reset to page 1); on In-Progress, use the client sort hook.
  // SortableTh expects { id, dir: 1 | -1 }.
  const headerSort = isServerPaged
    ? { id: allServerSort.key, dir: allServerSort.dir === "desc" ? -1 : 1 }
    : sort;
  const handleToggleSort = useCallback(
    (colId) => {
      if (isServerPaged) {
        setAllServerSort((prev) => {
          const dir = prev.key === colId && prev.dir === "asc" ? "desc" : "asc";
          return { key: colId, dir };
        });
        setPageAll(1);
      } else {
        toggleSort(colId);
      }
    },
    [isServerPaged, toggleSort]
  );

  const handleExportCsv = useCallback(async () => {
    const cols = mafiaColumns.filter((c) => c.type !== "none");
    if (!isServerPaged) {
      // In-Progress: small client-side set — export what's in view.
      exportRowsToCsv(`mafia-parts-${activeTab}.csv`, cols, viewRows);
      return;
    }
    // All tab is server-paged: fetch ALL matching rows (current search + sort),
    // then export. We page through the server rather than relying on the single
    // page held in memory, so the CSV reflects the full filtered list.
    try {
      setExporting(true);
      const fetchSize = 500;   // server clamps pageSize to 500
      const maxPages = 100;    // hard safety cap (≤ 50,000 rows)
      let collected = [];
      for (let pageNum = 1; pageNum <= maxPages; pageNum += 1) {
        const { items, total } = await getEditPartsList({
          page: pageNum,
          pageSize: fetchSize,
          search: searchAll,
          sort: allServerSort.key,
          sortDir: allServerSort.dir,
          filters: effectiveColumnFilters,   // R9: export the filtered set
        });
        collected = collected.concat(items || []);
        if (!items || items.length < fetchSize) break;   // last page
        if (collected.length >= (total || 0)) break;      // got everything
      }
      // Round 8 (Batch 2): resolve names before writing the CSV so the export
      // shows "First Last (id)" for Created By / Last Revised By (matches the
      // approved decision that export uses names). enrichRowsWithNames batches
      // + caches, so this is one extra call regardless of row count.
      const built = await enrichRowsWithNames(collected.map((item) => buildRow(item, true)));
      exportRowsToCsv(`mafia-parts-${activeTab}.csv`, cols, built);
    } catch (e) {
      console.error("CSV export failed:", e);
      await dialog.error("Could not export the full list. Please try again.");
    } finally {
      setExporting(false);
    }
  }, [isServerPaged, activeTab, viewRows, mafiaColumns, searchAll, allServerSort, serverColumnFilters, dialog]);

  const setSelectedFromRow = useCallback((row, tableType) => {
    if (!row) return;

    if (tableType === "all") {
      setSelectedRowKeyAll(row.rowKey);
      setSelectedRowAll(row);
      setSelectedRowKeyInProgress("");
      setSelectedRowInProgress(null);
    } else {
      setSelectedRowKeyInProgress(row.rowKey);
      setSelectedRowInProgress(row);
      setSelectedRowKeyAll("");
      setSelectedRowAll(null);
    }
  }, []);

  const openEditPart = useCallback(
    (row = selectedRow) => {
      if (!row?.partNumber) return;

      requestAnimationFrame(() => {
        if (typeof onOpenEditPart === "function") {
          onOpenEditPart({
            partNum: String(row.partNumber ?? ""),
            rev: Number(row.printRevision ?? 0),
            revMinor: Number(row.mafiaRevision ?? 0),
            isImported: false,
            isNew: false,
          });
        } else {
          console.warn("Edit Part callback is not configured.");
        }
      });
    },
    [onOpenEditPart, selectedRow]
  );

  const handleSearchKeyDown = useCallback(
    (e) => {
      if (e.key !== "Enter") return;
      if (currentFilteredRows.length === 0) return;

      const firstMatch = currentFilteredRows[0];
      const rowPage = Math.floor(currentFilteredRows.indexOf(firstMatch) / pageSize) + 1;

      if (activeTab === "all") {
        setPageAll(rowPage);
        setSelectedRowKeyAll(firstMatch.rowKey);
        setSelectedRowAll(firstMatch);
      } else {
        setPageInProgress(rowPage);
        setSelectedRowKeyInProgress(firstMatch.rowKey);
        setSelectedRowInProgress(firstMatch);
      }

      requestAnimationFrame(() => {
        const rowEl = rowRefs.current[firstMatch.rowKey];
        if (rowEl) {
          rowEl.scrollIntoView({ block: "nearest", behavior: "auto" });
        }
        if (currentSearchRef.current) {
          currentSearchRef.current.select();
        }
      });
    },
    [activeTab, currentFilteredRows, pageSize, currentSearchRef]
  );

  const handleRefresh = useCallback(async () => {
    await loadTabData(activeTab, true, true);
  }, [activeTab, loadTabData]);

  const switchTab = useCallback(
    (tab) => {
      if (tab === activeTab) return;
      setActiveTab(tab);
    },
    [activeTab]
  );

  const handlePageSizeChange = useCallback((e) => {
    const nextSize = Number(e.target.value) || DEFAULT_PAGE_SIZE;
    setPageSize(nextSize);
    setPageAll(1);
    setPageInProgress(1);
  }, []);

  const handleSearchChange = useCallback(
    (value) => {
      if (activeTab === "all") {
        setSearchAllInput(value);
      } else {
        setSearchInProgressInput(value);
      }
    },
    [activeTab]
  );

  const isCurrentTabLoading =
    loading && ((activeTab === "all" && !allLoaded) || (activeTab === "inprogress" && !inProgressLoaded));

  return (
    <div className="all-parts-page mafia-screen">
      <MafiaTheme />
      <MafiaPageHeader
        icon="ri-stack-line"
        eyebrow="MAFIA Aux"
        title="All Parts"
        subtitle="Browse the complete parts catalog. Double-click a row to open Edit Part."
        tone="blue"
        actions={
          <Button
            color="light"
            onClick={onBack}
            style={{
              background: "rgba(255,255,255,0.92)",
              border: "none",
              fontWeight: 500,
            }}
          >
            <i className="ri-arrow-left-line align-bottom me-1" />
            Back to Menu
          </Button>
        }
        breadcrumb={[
          { label: "Applications", to: "/" },
          { label: "MAFIA Aux", onClick: onBack },
          { label: "All Parts" },
        ]}
      />

      <Row className="g-4">
        <Col xl={12}>
          <Card className="border-0 shadow-sm">
            <CardHeader className="bg-white border-0 pt-4 px-4 pb-3">
              <div className="all-parts-toolbar-main">
                <div className="all-parts-toolbar-main-left">
                  <Button
                    color="primary"
                    className="all-parts-action-btn"
                    onClick={() => openEditPart()}
                    disabled={!selectedRow}
                  >
                    <i className="ri-edit-2-line all-parts-btn-icon me-1" />
                    Open Edit Part
                  </Button>
                </div>

                <div className="all-parts-toolbar-main-right">
                  {/* Round 9 (Batch 3): the broad global search was removed per
                      the approved decision — use the per-column filter row under
                      the table headers instead (backend-driven, exact + fast). */}
                  <span className="text-muted small d-none d-md-inline">
                    <i className="ri-filter-3-line me-1" />
                    Filter using the column boxes below
                  </span>
                </div>
              </div>

              <div className="all-parts-toolbar-utility">
                <Button
                  color="primary"
                  className="all-parts-action-btn all-parts-utility-btn"
                  onClick={handleRefresh}
                  disabled={refreshing}
                >
                  <i className="ri-refresh-line all-parts-btn-icon me-1" />
                  {refreshing ? "Refreshing..." : "Refresh"}
                </Button>

                <ResponsiveTabs
                  tabs={[
                    { key: "all",        label: "All Parts",   count: allRows.length },
                    { key: "inprogress", label: "In Progress", count: inProgressRows.length },
                  ]}
                  activeKey={activeTab}
                  onChange={switchTab}
                  variant="underline"
                  color="primary"
                />

                {activeTab === "all" && (
                  // Engineers 2026-10-14: approved revisions first; every revision on request
                  <div className="btn-group btn-group-sm" role="group" aria-label="Which revisions to list">
                    <Button color={approvedOnly ? "primary" : "light"} className={approvedOnly ? "" : "border"}
                      onClick={() => { if (!approvedOnly) { setApprovedOnly(true); setPageAll(1); } }}
                      title="Released MAFIA tables only">
                      <i className="ri-checkbox-circle-line align-bottom me-1" />Approved
                    </Button>
                    <Button color={approvedOnly ? "light" : "primary"} className={approvedOnly ? "border" : ""}
                      onClick={() => { if (approvedOnly) { setApprovedOnly(false); setPageAll(1); } }}
                      title="Every revision, released or not">
                      All revisions
                    </Button>
                  </div>
                )}

                <Badge color="light" className="all-parts-queue-badge">
                  Queue Size: {effectiveTotal}
                </Badge>

                <div className="all-parts-info-pill">
                  <span className="all-parts-info-label">Selected Part</span>
                  <span
                    className="all-parts-info-value"
                    title={selectedRow?.partNumber || "No selection"}
                  >
                    {selectedRow?.partNumber || "No selection"}
                  </span>
                </div>

                <div className="all-parts-info-pill">
                  <span className="all-parts-info-label">Revision</span>
                  <span className="all-parts-info-value">
                    {selectedRow
                      ? `Part ${partRevisionText(selectedRow.printRevision)} · MAFIA ${selectedRow.mafiaRevision ?? 0}`
                      : "—"}
                  </span>
                </div>

                <div className="all-parts-info-pill all-parts-page-size-pill">
                  <span className="all-parts-info-label">Rows / Page</span>
                  {/* Owner 2026-10-12: the native select showed stacked chevrons in the dark
                      themes and an unreadable menu in the light one; the themed select paints
                      control and menu from the theme like every other list on the dashboard. */}
                  <ThemedSelect
                    classNamePrefix="all-parts-page-size"
                    isSearchable={false}
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    options={PAGE_SIZE_OPTIONS.map((size) => ({ value: size, label: String(size) }))}
                    value={{ value: pageSize, label: String(pageSize) }}
                    onChange={(opt) => handlePageSizeChange({ target: { value: opt?.value } })}
                    styles={{
                      control: (base) => ({ ...base, minHeight: 28, minWidth: 84, border: 0, boxShadow: "none", background: "transparent", fontWeight: 700 }),
                      valueContainer: (base) => ({ ...base, padding: "0 4px" }),
                      indicatorSeparator: () => ({ display: "none" }),
                      dropdownIndicator: (base) => ({ ...base, padding: "0 4px" }),
                      menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                    }}
                  />
                </div>

                <div className="all-parts-refresh-note">
                  {activeTab === "all"
                    ? (approvedOnly ? "Approved (released) MAFIA tables" : "Every MAFIA revision, released or not")
                    : "Records still being worked on"}
                </div>
              </div>
            </CardHeader>

            <CardBody className="p-4">
              <div className="all-parts-table-shell">
                <div className="all-parts-table-scroll">
                  {/* Round 35 — CSV of the filtered + sorted parts list */}
                  <div className="d-flex justify-content-end mb-2">
                    <button type="button" className="btn btn-soft-primary btn-sm"
                      disabled={exporting}
                      onClick={handleExportCsv}>
                      <i className="ri-download-2-line align-bottom me-1" />
                      {exporting ? "Exporting…" : `Export CSV (${effectiveTotal})`}
                    </button>
                  </div>
                  <Table responsive={false} className="align-middle mb-0 all-parts-table">
                    <thead className="table-light">
                      <tr>
                        {mafiaColumns.map((c) => (
                          <SortableTh key={c.id} col={c} sort={headerSort} onToggle={handleToggleSort}
                            className={c.id === "sel" ? "all-parts-col-select" : ""} />
                        ))}
                      </tr>
                      {/* Fix (Batch 3.2): render the column filter row on BOTH tabs.
                          The All tab sends these to the server (Round 9); the
                          In-Progress tab filters client-side. The old guard hid it
                          on the server-paged All tab — which is exactly where you
                          expect the filters. */}
                      {/* Round 63: dynamic search — filters commit as you type
                          (debounced), matching every other MAFIA table. On the
                          All tab each commit is a server-side filtered fetch. */}
                      <ColumnFilterRow columns={mafiaColumns} filters={columnFilters} onChange={setColumnFilters} live />
                    </thead>
                    <tbody>
                      {isCurrentTabLoading ? (
                        <TableSkeleton
                          rows={6}
                          columns={activeTab === "all" ? 10 : 9}
                        />
                      ) : pagedRows.length === 0 ? (
                        <tr>
                          <td
                            colSpan={activeTab === "all" ? 10 : 9}
                            className="p-0"
                          >
                            <EmptyState
                              icon="ri-stack-line"
                              title="No parts match"
                              message={
                                Object.values(serverColumnFilters || {}).some((v) => String(v || "").trim() !== "") ||
                                Object.values(columnFilters || {}).some((v) => String(v || "").trim() !== "")
                                  ? "No parts match the current column filters. Try clearing or adjusting them."
                                  : "No parts in this view yet."
                              }
                              tone="secondary"
                            />
                          </td>
                        </tr>
                      ) : (
                        pagedRows.map((row) => (
                          <MemoizedPartRow
                            key={row.rowKey}
                            row={row}
                            isSelected={row.rowKey === selectedRowKey}
                            activeTab={activeTab}
                            setSelectedFromRow={setSelectedFromRow}
                            openEditPart={openEditPart}
                            rowRefs={rowRefs}
                          />
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>

              <div className="all-parts-pagination-bar">
                <div className="all-parts-pagination-info">
                  Showing {pageStart} - {pageEnd} of {effectiveTotal}
                </div>

                <div className="all-parts-pagination-controls">
                  <Button
                    color="light"
                    className="all-parts-pagination-btn"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage <= 1}
                  >
                    First
                  </Button>
                  <Button
                    color="light"
                    className="all-parts-pagination-btn"
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={currentPage <= 1}
                  >
                    Prev
                  </Button>

                  <div className="all-parts-page-indicator">
                    Page {currentPage} of {totalPages}
                  </div>

                  <Button
                    color="light"
                    className="all-parts-pagination-btn"
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                    }
                    disabled={currentPage >= totalPages}
                  >
                    Next
                  </Button>
                  <Button
                    color="light"
                    className="all-parts-pagination-btn"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage >= totalPages}
                  >
                    Last
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <style>
        {`
          .all-parts-toolbar-main {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 1rem;
            flex-wrap: wrap;
            margin-bottom: 0.9rem;
          }

          .all-parts-toolbar-main-left {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            flex-wrap: wrap;
          }

          .all-parts-toolbar-main-right {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            flex: 0 0 360px;
            min-width: 280px;
          }

          .all-parts-toolbar-utility {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            flex-wrap: wrap;
            padding-top: 0.25rem;
          }

          .all-parts-search-group {
            width: 100%;
          }

          .all-parts-action-btn {
            white-space: nowrap;
          }
          /* Make disabled primary buttons obviously disabled (not faded primary) */
          .all-parts-action-btn:disabled,
          .all-parts-action-btn.disabled {
            background-color: var(--ssw-surface-3, #e9ebec) !important;
            border-color: var(--ssw-line, #e9ebec) !important;
            color: var(--ssw-fg-2, #878a99) !important;
            opacity: 1 !important;
          }


          .all-parts-utility-btn {
            min-width: 110px;
          }

          .all-parts-btn-icon {
            font-size: 0.95rem;
          }

          .btn-soft-dark {
            background: var(--ssw-surface-3, #eff2f7);
            color: var(--ssw-fg-1, #212529);
            border: 1px solid var(--ssw-line, #e9ebec);
          }

          .btn-soft-dark:hover,
          .btn-soft-dark:focus {
            background: var(--ssw-surface-3, #e9ebec);
            color: var(--ssw-fg-1, #212529);
            border-color: var(--ssw-line, #dee2e6);
          }

          .all-parts-nav-pills-placeholder {
            /* nav-pills CSS removed — replaced with ResponsiveTabs (Pass 6) */
          }

          .all-parts-queue-badge {
            display: inline-flex;
            align-items: center;
            min-height: 34px;
            border: 1px solid var(--ssw-line, #e9ebec);
            padding: 0.35rem 0.7rem;
            border-radius: 0.5rem;
            font-family: inherit;
            font-size: 0.8125rem;
            font-weight: 600;
            color: var(--ssw-fg-1, #495057);
            background: var(--ssw-surface-3, #f3f6f9);
          }

          .all-parts-info-pill {
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            min-height: 34px;
            padding: 0.3rem 0.75rem;
            border: 1px solid var(--ssw-line, #e9ebec);
            border-radius: 0.5rem;
            background: var(--ssw-surface-3, #f3f6f9);
            font-family: inherit;
            max-width: 300px;
          }

          .all-parts-page-size-pill {
            padding-right: 0.5rem;
          }

          .all-parts-page-size-select {
            min-width: 84px;
            border: 0;
            background: transparent;
            font-family: inherit;
            font-weight: 700;
            color: var(--ssw-fg-1, #212529);
            box-shadow: none !important;
            padding-top: 0;
            padding-bottom: 0;
          }

          .all-parts-info-label {
            font-family: inherit;
            font-size: 0.6875rem;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            color: var(--ssw-fg-2, #878a99);
            white-space: nowrap;
            line-height: 1.1;
          }

          .all-parts-info-value {
            font-family: inherit;
            font-size: 0.8125rem;
            font-weight: 600;
            color: var(--ssw-fg-1, #495057);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .all-parts-refresh-note {
            min-height: 38px;
            display: inline-flex;
            align-items: center;
            padding: 0.45rem 0.2rem;
            font-size: 0.84rem;
            color: var(--ssw-fg-2, #878a99);
            font-weight: 500;
          }

          .all-parts-table-shell {
            border: 1px solid var(--ssw-line, #e9ebec);
            border-radius: 1rem;
            background: var(--ssw-surface, #fff);
            box-shadow: inset 0 1px 0 rgba(255,255,255,0.6);
          }

          .all-parts-table-scroll {
          max-height: calc(100vh - 240px);
          min-height: 320px;
          overflow-x: auto;
          overflow-y: auto;
        }

          .all-parts-table {
            min-width: 1120px;
          }

          .all-parts-table thead th {
            position: sticky;
            top: 0;
            z-index: 2;
            background: var(--ssw-surface-3, #f3f6f9);
            white-space: nowrap;
          }

          .all-parts-col-select {
            width: 70px;
            text-align: center;
          }

          .all-parts-table tbody td {
            padding: 0.9rem 0.9rem;
            border-top: 1px solid var(--ssw-line, #eef2f7);
            vertical-align: middle;
            color: var(--ssw-fg-1, #495057);
            white-space: nowrap;
          }

          .all-parts-data-row {
            transition: background-color 0.12s ease, box-shadow 0.12s ease;
            cursor: pointer;
          }

          .all-parts-data-row:nth-of-type(even) {
            background: var(--ssw-surface-3, #fbfcfe);
          }

          .all-parts-data-row:hover {
            background: var(--ssw-surface-3, #f5f9ff);
          }

          .all-parts-data-row.selected {
            background: rgba(13, 110, 253, 0.08);
            box-shadow: inset 4px 0 0 #0d6efd;
          }

          .all-parts-status {
            display: inline-flex;
            align-items: center;
            gap: 0.4rem;
            padding: 0.35rem 0.65rem;
            border-radius: 999px;
            font-size: 0.78rem;
            font-weight: 700;
          }

          .all-parts-status.yes {
            background: rgba(25, 135, 84, 0.12);
            color: var(--ssw-ink-green, #198754);
          }

          .all-parts-status.no {
            background: rgba(220, 53, 69, 0.1);
            color: var(--ssw-ink-red, #dc3545);
          }

          .all-parts-status.na {
            background: rgba(100, 116, 139, 0.12);
            color: var(--ssw-fg-2, #878a99);
          }

          .all-parts-pagination-bar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 1rem;
            flex-wrap: wrap;
            padding-top: 1rem;
          }

          .all-parts-pagination-info {
            color: var(--ssw-fg-1, #495057);
            font-size: 0.9rem;
            font-weight: 600;
          }

          .all-parts-pagination-controls {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            flex-wrap: wrap;
          }

          .all-parts-pagination-btn {
            border-radius: 0.6rem;
            font-weight: 600;
            min-width: 70px;
          }

          .all-parts-page-indicator {
            min-width: 120px;
            text-align: center;
            font-weight: 700;
            color: var(--ssw-fg-1, #212529);
          }

          @media (max-width: 1199.98px) {
            .all-parts-toolbar-main {
              flex-direction: column;
              align-items: stretch;
            }

            .all-parts-toolbar-main-right {
              flex: 1 1 auto;
              min-width: 100%;
            }
          }

          @media (max-width: 991.98px) {
            .all-parts-info-pill {
            display: inline-flex;
            align-items: center;
            gap: 0.4rem;
            min-height: 32px;
            padding: 0.3rem 0.65rem;
            border: 1px solid var(--ssw-line, #e9ebec);
            border-radius: 0.375rem;
            background: var(--ssw-surface-3, #f3f6f9);
            max-width: 300px;
          }

            .all-parts-toolbar-utility {
              align-items: stretch;
            }

            .all-parts-pagination-bar {
              flex-direction: column;
              align-items: stretch;
            }

            .all-parts-pagination-controls {
              justify-content: flex-start;
            }
          }
        `}
      </style>
    </div>
  );
}

const MemoizedPartRow = memo(function PartRow({
  row,
  isSelected,
  activeTab,
  setSelectedFromRow,
  openEditPart,
  rowRefs,
}) {
  const tableType = activeTab === "all" ? "all" : "inprogress";

  const handleSelect = useCallback(() => {
    setSelectedFromRow(row, tableType);
  }, [row, tableType, setSelectedFromRow]);

  const handleDoubleClick = useCallback(() => {
    openEditPart(row);
  }, [openEditPart, row]);

  return (
    <tr
      ref={(el) => {
        if (el) {
          rowRefs.current[row.rowKey] = el;
        } else {
          delete rowRefs.current[row.rowKey];
        }
      }}
      tabIndex={0}
      className={classNames("all-parts-data-row", { selected: isSelected })}
      onClick={handleSelect}
      onDoubleClick={handleDoubleClick}
    >
      <td className="all-parts-col-select">
        <div className="form-check d-flex justify-content-center m-0">
          <Input
            type="radio"
            name={`row-select-${activeTab}`}
            checked={isSelected}
            onChange={handleSelect}
          />
        </div>
      </td>
      <td>
        <div className="fw-semibold text-dark">{row.partNumber || ""}</div>
      </td>
      <td>{partRevisionText(row.printRevision)}</td>
      <td>{row.mafiaRevision ?? ""}</td>
      <td>{row.createdByDisplay || row.createdBy || ""}</td>
      {activeTab === "all" && <td>{row.lastRevisedByDisplay || row.lastRevisedBy || ""}</td>}
      <td>{row.verifiedByDisplay || row.verifiedBy || ""}</td>
      <td>{row.displayTimeStamp}</td>
      <td>
        <MemoizedStatusBadge
          value={row.verified}
          positiveLabel="Yes"
          negativeLabel="No"
        />
      </td>
      <td>
        <MemoizedStatusBadge
          value={row.released}
          positiveLabel="Yes"
          negativeLabel="No"
        />
      </td>
    </tr>
  );
});

const MemoizedStatusBadge = memo(function StatusBadge({
  value,
  positiveLabel,
  negativeLabel,
}) {
  if (value === true) {
    return (
      <span className="all-parts-status yes">
        <i className="ri-checkbox-circle-fill" /> {positiveLabel}
      </span>
    );
  }

  if (value === false) {
    return (
      <span className="all-parts-status no">
        <i className="ri-close-circle-fill" /> {negativeLabel}
      </span>
    );
  }

  return <span className="all-parts-status na">—</span>;
});

function buildRow(item, includeLastRevisedBy = false) {
  const partNumber = item.partNumber ?? "";
  const printRevision = item.printRevision ?? 0;
  const mafiaRevision = item.mafiaRevision ?? 0;
  const createdBy = item.createdBy ?? "";
  const lastRevisedBy = includeLastRevisedBy ? item.lastRevisedBy ?? "" : "";
  const verifiedBy = item.verifiedBy ?? "";
  const displayTimeStamp = formatDateTime(item.timeStamp);

  return {
    partNumber,
    printRevision,
    mafiaRevision,
    createdBy,
    lastRevisedBy,
    verifiedBy,
    verifiedByDisplay: verifiedBy,
    // Round 8 (Batch 2): display fields the grid renders. They start equal to
    // the raw value and get replaced with "First Last (id)" once the async
    // name resolution completes (see enrichRowsWithNames). Rendering these
    // instead of the raw createdBy/lastRevisedBy is what turns "3325" into a
    // name without changing what's stored.
    createdByDisplay: createdBy,
    lastRevisedByDisplay: lastRevisedBy,
    timeStamp: item.timeStamp,
    displayTimeStamp,
    verified: item.verified,
    released: item.released,
    rowKey: `${partNumber}-${printRevision}-${mafiaRevision}`,
    searchText: [
      partNumber,
      String(printRevision),
      String(mafiaRevision),
      createdBy,
      lastRevisedBy,
      displayTimeStamp,
    ]
      .join(" ")
      .toUpperCase(),
  };
}

// Round 8 (Batch 2): resolve the distinct Created By / Last Revised By raw ids
// in a built row set to display names, in ONE batched backend call, then return
// a NEW array with createdByDisplay/lastRevisedByDisplay populated. New row
// object refs ensure the memoized rows re-render with the resolved names. Soft:
// on any failure the resolver returns Unknown/raw, so this never throws.
async function enrichRowsWithNames(rows) {
  if (!rows || rows.length === 0) return rows;
  const raws = [];
  for (const r of rows) {
    if (r.createdBy) raws.push(r.createdBy);
    if (r.lastRevisedBy) raws.push(r.lastRevisedBy);
    if (r.verifiedBy) raws.push(r.verifiedBy);
  }
  if (raws.length === 0) return rows;

  const map = await resolveEmployeeNames(raws);

  return rows.map((r) => ({
    ...r,
    createdByDisplay: r.createdBy ? (map.get(r.createdBy) || r.createdBy) : "",
    lastRevisedByDisplay: r.lastRevisedBy ? (map.get(r.lastRevisedBy) || r.lastRevisedBy) : "",
    verifiedByDisplay: r.verifiedBy ? (map.get(r.verifiedBy) || r.verifiedBy) : "",
  }));
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}

async function getEditPartsList({ page, pageSize, search, sort, sortDir, filters } = {}) {
  // Server-side pagination: ask for one page with server sort + column filters.
  const params = new URLSearchParams();
  if (page) params.set("page", String(page));
  if (pageSize) params.set("pageSize", String(pageSize));
  if (search) params.set("search", search);
  if (sort) params.set("sort", sort);
  if (sortDir) params.set("sortDir", sortDir);

  // Round 9 (Batch 3): map the grid's per-column filter values to the backend
  // filter params. Column ids come from the mafiaColumns definitions.
  if (filters) {
    const m = {
      partNumber: "fPartNum",
      printRevision: "fRev",
      mafiaRevision: "fRevMinor",
      createdBy: "fCreatedBy",
      lastRevisedBy: "fRevisedBy",
      verified: "fVerified",
      released: "fReleased",
    };
    for (const [colId, param] of Object.entries(m)) {
      let v = filters[colId];
      if (v == null || String(v).trim() === "") continue;
      v = String(v).trim();
      // The list prints the part revision with two digits ("01"); the database holds 1.
      if (colId === "printRevision") v = v.replace(/^0+(?=d)/, "");
      params.set(param, v);
    }
  }

  const qs = params.toString();
  const response = await api.get(`mafia/edit-parts${qs ? `?${qs}` : ""}`);
  // Endpoint returns { items, totalCount, page, pageSize } when paged.
  const data = response.data;
  if (data && Array.isArray(data.items)) {
    return { items: data.items, total: data.totalCount ?? data.items.length };
  }
  // Back-compat: a bare array (non-paged response).
  const arr = Array.isArray(data) ? data : [];
  return { items: arr, total: arr.length };
}

async function getEditPartsListInProgress() {
  const response = await api.get("mafia/edit-parts-inprogress");
  return (response.data || []).map((item) => buildRow(item, false));
}